import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Input, Modal, Segmented, Select, Space, Switch, Tooltip, Typography } from 'antd';

import EventFormElement from '../events/EventFormElement';
import FormulaInput from '../common/FormulaInput';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { CardOptionLabel } from '../cards/CardVisuals';
import { TRANSFORM_LABELS } from '../cards/DrivenFromModal';
import { optionSearchFilter } from '../Input';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { QUICK_ADD_SCOPE } from '../../hooks/useQuickAddTemplates';
import {
	createQuickAddTemplate,
	demoteQuickAddTemplate,
	previewQuickAddTemplate,
	promoteQuickAddTemplate,
	updateQuickAddTemplate,
} from '../../services/quickAddTemplates';
import { describeFunctions } from '../../utils/formula';
import {
	analyzeFormula,
	buildQuickAddTokens,
	createQuickAddRefResolver,
	isMeIdFormula,
	isUserIdFormula,
	ME_ID_TOKEN,
	USER_ID_TOKEN,
	formatCalculatedValue,
	formulaElementResolver,
	mergedElementsForCard,
} from '../../utils/formulaTokens';
import { getElementInitialValue, isElementValueEmpty } from '../../utils/elements';
import { collectionZone } from '../../utils/collectionTimezone';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { canManage } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { meElementsOf } from '../../utils/userElements';
import { lookupFamilyHasIdentity } from '../../utils/lookupMe';
import { createLookupResolver } from '../../utils/lookups';
import useLinkedItems from '../../hooks/useLinkedItems';
import TemplateTagsField from '../tags/TemplateTagsField';
import { copyableOnDuplicate, notCopiedReason } from '../../utils/duplicateCopy';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import { entriesOf } from '../../utils/repeatingGroupEntries';
import { presetForWire, presetGroup, presetSummary } from '../../utils/quickAddGroupPresets';

/**
 * Authors ONE quick add template — the named bundle of element defaults that
 * opens the new-item form pre-filled. Three ways in:
 *
 *   capture  "Save as quick add…" on the item form: every non-empty value on
 *            the form becomes a Fixed default, empties Skip. Name it, done.
 *   edit     an existing template (the Quick add tab, the Manage… modal).
 *   create   from scratch: pick a writable card, then the element rows.
 *
 * Each element of the card (its parent chain merged; driven elements left
 * out — the server owns those) is one row: Skip (the card's own default
 * applies) · Fixed (the element's usual input, so a lookup is picked the way
 * it is picked on the form) · Calculated (a formula in the calculated-element
 * language, with the template's own roots: {{el}}, {{lookup.el}},
 * {{last.el}}, {{user.name}}). A formula that reads the last item opens the
 * "Last item" panel — match on which Fixed elements, only the caller's own.
 *
 * Validation runs live through the SAME formula module the API runs; Preview
 * asks the API to resolve the unsaved body exactly as a launch would, so
 * "what will this fill in?" is answered before anything is stored.
 *
 * New templates are created in the user tier; a manager who picks "Everyone"
 * gets the create followed by a promote (the API's own two steps).
 *
 * "Duplicate profile" turns the template into one that copies from the item
 * being duplicated (`copyAll` — keepr-api docs/SCHEMA.md "Duplicate
 * profiles"): every row then starts at Copy, Skip means "leave blank", and
 * the formulas may read `{{source.*}}`. A profile launches from an item's
 * Duplicate ▾, never from a quick add row; a manager may make a pinned one
 * the card's default for Duplicate. A template that only READS
 * `{{source.*}}` (no copyAll) is a profile too, and opens with the switch
 * on; it stays copy-nothing-else until a row is set to Copy. Elements the card marks "Leave blank
 * when duplicating" (and the account link) are never copied, so they offer
 * no Copy — set them Fixed or Calculated to fill them anyway.
 *
 * A REPEATING GROUP (KPR-1053) is Skip or Fixed, never Calculated: Fixed opens
 * the item form's own entry editor, so the template holds the entries a new
 * item starts with — a checklist under its section headings — and the body
 * sends them without ids (keepr gives each its own when the item is saved;
 * utils/quickAddGroupPresets.js).
 */

export const DUPLICATE_NAME_MESSAGE = 'You already have a quick add with this name';

export const ROW_MODE = { COPY: 'copy', SKIP: 'skip', STATIC: 'static', FORMULA: 'formula' };

const MODE_OPTIONS = [
	{ value: ROW_MODE.SKIP, label: 'Skip' },
	{ value: ROW_MODE.STATIC, label: 'Fixed' },
	{ value: ROW_MODE.FORMULA, label: 'Calculated' },
];
// A duplicate profile's rows: Copy first — the profile copies everything
// unless told otherwise.
const PROFILE_MODE_OPTIONS = [
	{ value: ROW_MODE.COPY, label: 'Copy' },
	...MODE_OPTIONS,
];
// A repeating group takes fixed entries only: nothing computes a list.
const GROUP_MODE_OPTIONS = MODE_OPTIONS.filter(option => option.value !== ROW_MODE.FORMULA);
const GROUP_PROFILE_MODE_OPTIONS = PROFILE_MODE_OPTIONS.filter(option => option.value !== ROW_MODE.FORMULA);
const modeOptionsFor = (element, offersCopy) => (isRepeatingGroup(element)
	? (offersCopy ? GROUP_PROFILE_MODE_OPTIONS : GROUP_MODE_OPTIONS)
	: (offersCopy ? PROFILE_MODE_OPTIONS : MODE_OPTIONS));

// Can a copyAll profile copy this element? The one rule every duplicate
// shares (utils/duplicateCopy.js, the API's copyableOnDuplicate); driven and
// numbered elements never reach it here — they are not rows at all.
const isCopyable = copyableOnDuplicate;

// A template default that cannot be computed leaves the field to the card's
// own default — 'empty' is the template rule; counting blanks as zero is the
// opt-in, the reverse of a calculated element.
const BLANKS_OPTIONS = [
	{ value: 'empty', label: 'Leave blank' },
	{ value: 'zero', label: 'Count as zero' },
];

const TIER_OPTIONS = [
	{ value: QUICK_ADD_SCOPE.USER, label: 'Just me' },
	{ value: QUICK_ADD_SCOPE.COLLECTION, label: 'Everyone' },
];

// The elements a "last item" match can key on: a Fixed value that names a
// thing (the resident, the method), never a free number or a date.
const MATCHABLE_TYPES = new Set(['card-lookup', 'choice', 'text-small']);

const labelOf = element => element?.label?.singular || element?.name || '';
// Driven OR server-numbered: a template cannot pre-fill either, and the
// API refuses a default that names one.
const isDriven = element => !!(element?.options?.drivenFrom || element?.options?.sequence);

const functionOptions = () => {
	const groups = new Map();
	describeFunctions().forEach(fn => {
		if (!groups.has(fn.group)) groups.set(fn.group, []);
		groups.get(fn.group).push({
			value: fn.name,
			label: `${fn.name}(${fn.args.join(', ')})`,
			searchText: `${fn.name} ${fn.description}`.toLowerCase(),
			description: fn.description,
		});
	});
	return [...groups.entries()].map(([group, options]) => ({ label: group, options }));
};

/** Does this analysis read the last item? (An element literally named `last` wins.) */
const readsLastItem = (analysis, text, names) => {
	if (names.has('last')) return false;
	if (analysis?.ok || analysis?.refs) {
		return (analysis.refs || []).some(ref => ref.path.length === 2 && ref.path[0] === 'last');
	}
	return /\{\{\s*last\./.test(text || '');
};

/**
 * The stored template → editor rows. In a copyAll profile an element the
 * template does not set is COPIED, and a stored `skip` is Skip.
 */
const rowsFromTemplate = (elements, template, copyAll = false) => {
	const defaults = template?.defaults || {};
	const rows = {};
	elements.forEach(el => {
		const stored = defaults[el.name];
		if (copyAll && !stored && isCopyable(el)) {
			rows[el.name] = { mode: ROW_MODE.COPY, value: undefined, formula: '', blanks: 'empty', transform: '' };
			return;
		}
		if (stored?.kind === 'formula') {
			rows[el.name] = {
				mode: ROW_MODE.FORMULA,
				value: undefined,
				formula: stored.formula || '',
				blanks: stored.blanks === 'zero' ? 'zero' : 'empty',
				transform: stored.transform?.type || '',
			};
		} else if (stored?.kind === 'static') {
			rows[el.name] = { mode: ROW_MODE.STATIC, value: stored.value, formula: '', blanks: 'empty', transform: '' };
		} else {
			rows[el.name] = { mode: ROW_MODE.SKIP, value: undefined, formula: '', blanks: 'empty', transform: '' };
		}
	});
	return rows;
};

/**
 * Is this value only what the card definition would seed on a NEW item
 * (defaultToToday / defaultToNow, or its defaultValue — any type's since
 * data types PR 4, a relative date included)? Capturing it as Fixed would
 * freeze today's date into every launch, or restate the card's own default;
 * Skip lets the card re-stamp it each time, which is what the author meant.
 */
const isCardSeededDefault = (el, value, zone) => {
	const options = el?.options || {};
	if ((el?.dataType === 'date' && options.defaultToToday)
		|| ((el?.dataType === 'date-time' || el?.dataType === 'time') && options.defaultToNow)) {
		return true;
	}
	// In the collection's zone, as the form that seeded `value` read it (B11).
	const seeded = getElementInitialValue(el, { isNewRecord: true, zone });
	if (seeded === undefined || seeded === null || seeded === '') return false;
	if (Array.isArray(seeded)) {
		return Array.isArray(value) && value.length === seeded.length && seeded.every((entry, i) => entry === value[i]);
	}
	if (seeded && typeof seeded === 'object') {
		if (!value || typeof value !== 'object') return false;
		// Money compares by its stored pair, a measurement by its own.
		if (el?.dataType === 'currency') return seeded.amount === value.amount && seeded.currency === value.currency;
		return seeded.value === value.value && seeded.unit === value.unit;
	}
	return seeded === value;
};

/** The form's live values → editor rows (capture). */
const rowsFromValues = (elements, values, zone) => {
	const rows = {};
	elements.forEach(el => {
		// A group's entries come over without the ids the item gave them: a
		// template's entries are nobody's yet (KPR-1053).
		const value = isRepeatingGroup(el)
			? entriesOf(values?.[el.name]).map(({ id: _issued, ...entry }) => entry)
			: values?.[el.name];
		const empty = isElementValueEmpty(el, value) || isCardSeededDefault(el, value, zone);
		rows[el.name] = {
			mode: empty ? ROW_MODE.SKIP : ROW_MODE.STATIC,
			value: empty ? undefined : value,
			formula: '',
			blanks: 'empty',
			transform: '',
		};
	});
	return rows;
};

/**
 * One Calculated row: the token editor, its function picker, the live
 * verdict, the blanks rule and (when the result needs converting) the
 * conversion. Analysis is the parent's — it also drives the Last item panel.
 */
function FormulaRow({ element, row, analysis, tokens, functionGroups, onChange }) {
	const editorRef = useRef(null);
	const [pickerValue, setPickerValue] = useState(undefined);

	const legalTransforms = analysis.ok ? (analysis.target.transforms || ['none']) : [];
	// Keep the transform legal for the current result: a single legal one is
	// chosen; a stale one is replaced rather than silently saved.
	useEffect(() => {
		if (!analysis.ok) return;
		const current = row.transform;
		if (current && legalTransforms.includes(current)) return;
		onChange({ transform: legalTransforms.length ? legalTransforms[0] : '' });
	}, [analysis.ok, legalTransforms.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

	const insertFunction = useCallback(name => {
		if (!name) return;
		const fn = describeFunctions().find(entry => entry.name === name);
		editorRef.current?.insertText(`${name}()`, { caretBack: fn && fn.args.length === 0 ? 0 : 1 });
		setPickerValue(undefined);
	}, []);

	const label = labelOf(element);
	const verdict = (() => {
		if (analysis.empty) return null;
		if (!analysis.ok) {
			return <Alert type="error" showIcon message={analysis.error?.message || 'This formula is not valid yet.'} />;
		}
		const conversion = row.transform && row.transform !== 'none' ? ` — ${TRANSFORM_LABELS[row.transform] || row.transform}` : '';
		return (
			<Alert
				type="success"
				showIcon
				message={`Result: ${analysis.typeLabel}, stored in ${label}${conversion}.`}
				description={analysis.target?.note || null}
			/>
		);
	})();

	return (
		<Space direction="vertical" size={8} style={{ width: '100%' }}>
			<FormulaInput
				ref={editorRef}
				value={row.formula}
				onChange={formula => onChange({ formula })}
				tokens={tokens}
				ariaLabel={`${label} formula`}
				placeholder="Type a value, or pick from the menu: the last reading, your name"
			/>
			<div className="quick-add-row__toolbar">
				<Select
					className="quick-add-row__function-picker"
					size="small"
					showSearch
					placeholder="Insert a function…"
					value={pickerValue}
					onChange={insertFunction}
					options={functionGroups}
					optionFilterProp="searchText"
					filterOption={(input, option) => (option?.searchText || '').includes(String(input || '').toLowerCase())}
					optionRender={option => (
						<Tooltip title={option.data.description} placement="right">
							<span>{option.data.label}</span>
						</Tooltip>
					)}
					popupMatchSelectWidth={false}
					aria-label={`Insert a function into ${label} formula`}
				/>
				<Segmented
					size="small"
					aria-label={`${label}: when an input is blank`}
					value={row.blanks}
					onChange={blanks => onChange({ blanks })}
					options={BLANKS_OPTIONS}
				/>
				{analysis.ok && legalTransforms.length > 1 ? (
					<Select
						size="small"
						aria-label={`${label} conversion`}
						value={row.transform || undefined}
						onChange={transform => onChange({ transform })}
						options={legalTransforms.map(name => ({ value: name, label: TRANSFORM_LABELS[name] || name }))}
						popupMatchSelectWidth={false}
					/>
				) : null}
			</div>
			{verdict}
		</Space>
	);
}

export default function QuickAddTemplateModal({
	open,
	onCancel,
	onSaved,
	mode = 'create',
	collectionId,
	collection = null,
	card = null,
	template = null,
	initialElements = null,
	cardOptions = [],
	// Optional: the useQuickAddTemplates store of the surface that opened
	// the modal, so its list updates optimistically. Without it the services
	// are called directly (the item form's capture path has no list).
	store = null,
}) {
	const { message } = App.useApp();
	const contextCollection = useCollectionContext()?.collection || null;
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const cachedCards = cardDefinitions?.cards;
	const isManager = canManage(collection);

	const [cardId, setCardId] = useState();
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState(null);
	const [tier, setTier] = useState(QUICK_ADD_SCOPE.USER);
	const [rows, setRows] = useState({});
	const [lastItem, setLastItem] = useState({ matchElements: [], mine: false });
	// The tags the new item gets (T9): collection tags, item tags and — on a
	// personal quick add only — this person's own private tags.
	const [tagIds, setTagIds] = useState([]);
	const [formError, setFormError] = useState(null);
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const [saving, setSaving] = useState(false);
	// Duplicate profile mode, and the card's default for Duplicate. `profile`
	// is "this template copies from an item" — the API's duplicateProfile,
	// which a template also is when it only READS {{source.*}}; `copyAll` is
	// the stored "copy everything else" it may or may not add. They were one
	// flag, and a {{source.*}}-only profile then opened with the switch OFF.
	const [profile, setProfile] = useState(false);
	const [copyAll, setCopyAll] = useState(false);
	const [defaultForDuplicate, setDefaultForDuplicate] = useState(false);
	// Bumped wherever the modal seeds itself, so the seeded state is the
	// baseline the unsaved-changes guard measures typing against.
	const [seedVersion, setSeedVersion] = useState(0);

	// The card: given (capture/edit), from the template (edit), or picked.
	const activeCard = useMemo(() => {
		if (card) return card;
		const id = normalizeId(template?.card_id) || normalizeId(cardId);
		if (!id) return null;
		return cardsById?.[id] || (cardOptions || []).find(opt => normalizeId(opt.value) === id)?.card || null;
	}, [card, template?.card_id, cardId, cardsById, cardOptions]);
	const activeCardId = normalizeId(activeCard?._id);

	// Last-wins over the app cache, falling back to the card in hand.
	const getElementsForCard = useMemo(
		() => formulaElementResolver({ cardsById, elementSetsById }, activeCard ? [activeCard] : []),
		[cardsById, elementSetsById, activeCard]
	);
	const mergedElements = useMemo(() => {
		if (!activeCard) return [];
		const resolved = activeCardId ? getElementsForCard(activeCardId) : [];
		return resolved.length ? resolved : mergedElementsForCard({
			ownElements: activeCard.elements || [],
			parentCardId: activeCard.parentCardId,
			getElementsForCard,
		});
	}, [activeCard, activeCardId, getElementsForCard]);
	// Driven elements never take a template default — the server owns them.
	// Never a file element (PR 10b): a quick add template holds no file — its
	// value is an attachment of one item, which a template cannot hand out.
	// Never a "Me too" count (KPR-390): keepr counts it from the people who
	// confirm, a new item starts with none, and the API refuses a template
	// that presets one (keepr-api utils/quickAddTemplates/validate.js).
	const editableElements = useMemo(() => mergedElements.filter(el => el?.name && !isDriven(el) && el.dataType !== 'file' && el.dataType !== 'confirmations'), [mergedElements]);
	// What {{me.<element>}} may read: the elements of the collection's cards
	// that carry an account link (the cards the caller's own record can be
	// of). Empty when none does — the token group is then not offered.
	const meElements = useMemo(() => {
		const memberIds = collection ? getCollectionMemberCardIds(collection, cachedCards || []) : null;
		return meElementsOf({ cards: cachedCards || [], cardIds: memberIds?.size ? memberIds : null, getElementsForCard });
	}, [collection, cachedCards, getElementsForCard]);
	const elementNames = useMemo(() => new Set(mergedElements.map(el => el.name)), [mergedElements]);
	// Can this lookup element take {{me.id}}? Its card (or a card above or
	// under it) must carry an account link — the API's 400
	// default_to_me_unavailable otherwise (utils/lookupMe.js).
	const lookupMeAvailable = useCallback(el => (
		el?.dataType === 'card-lookup'
		&& lookupFamilyHasIdentity({ cardsById, elementSetsById, cardId: el?.options?.lookupCardId })
	), [cardsById, elementSetsById]);

	// Opening (re)seeds from the mode — Cancel must discard edits. Keyed on
	// the template's ID, not its object: a list refresh behind the modal must
	// not wipe what the user is typing.
	const templateRef = useRef(template);
	templateRef.current = template;
	const templateKey = normalizeId(template?._id) || '';
	useEffect(() => {
		if (!open) return;
		const stored = templateRef.current;
		setNameError(null);
		setFormError(null);
		setPreview(null);
		setCardId(undefined);
		setProfile(mode === 'edit' && (stored?.copyAll === true || stored?.duplicateProfile === true));
		setCopyAll(mode === 'edit' && stored?.copyAll === true);
		setDefaultForDuplicate(mode === 'edit' && stored?.defaultForDuplicate === true);
		setSeedVersion(version => version + 1);
		setTagIds(mode === 'edit' && Array.isArray(stored?.tagIds) ? stored.tagIds : []);
		if (mode === 'edit' && stored) {
			setName(stored.name || '');
			setTier(stored.scope === QUICK_ADD_SCOPE.COLLECTION ? QUICK_ADD_SCOPE.COLLECTION : QUICK_ADD_SCOPE.USER);
			setLastItem({
				matchElements: Array.isArray(stored.lastItem?.matchElements) ? stored.lastItem.matchElements : [],
				mine: !!stored.lastItem?.mine,
			});
		} else {
			setName('');
			setTier(QUICK_ADD_SCOPE.USER);
			setLastItem({ matchElements: [], mine: false });
		}
	}, [open, mode, templateKey]);

	// Rows follow the card's elements: seeded once the elements are known
	// (the cache may still be loading when the modal opens), and rebuilt when
	// a create-mode pick changes the card.
	const seededForRef = useRef(null);
	const seededCardRef = useRef(undefined);
	// The form's live values are read once, when the rows seed: the modal
	// sits over a form whose values must not re-seed edits made in here.
	const initialElementsRef = useRef(initialElements);
	initialElementsRef.current = initialElements;
	// The zone the item form seeded its defaults in (B11): the collection's,
	// from the prop when it carries the GET's answer, else the layout's.
	const zoneRef = useRef(null);
	zoneRef.current = collectionZone(collection?.timezoneResolved ? collection : contextCollection);
	useEffect(() => {
		if (!open) { seededForRef.current = null; seededCardRef.current = undefined; return; }
		const key = `${mode}:${templateKey}:${activeCardId}:${editableElements.map(el => el.name).join(',')}`;
		if (seededForRef.current === key) return;
		seededForRef.current = key;
		// A create-mode card pick rebuilds the rows, but it is the person's
		// choice, not a seed: it stays unsaved work.
		const pickedCard = mode === 'create' && seededCardRef.current !== undefined && seededCardRef.current !== activeCardId;
		seededCardRef.current = activeCardId;
		if (mode === 'edit') setRows(rowsFromTemplate(editableElements, templateRef.current, templateRef.current?.copyAll === true));
		else if (mode === 'capture') setRows(rowsFromValues(editableElements, initialElementsRef.current, zoneRef.current));
		else setRows(rowsFromTemplate(editableElements, null));
		if (!pickedCard) setSeedVersion(version => version + 1);
	}, [open, mode, templateKey, activeCardId, editableElements]);

	const dirtySnapshot = useMemo(
		() => ({ cardId, name, tier, rows, lastItem, profile, copyAll, defaultForDuplicate, tagIds }),
		[cardId, name, tier, rows, lastItem, profile, copyAll, defaultForDuplicate, tagIds]
	);
	const { resetDirty, guardDiscard } = useDirtyForm(dirtySnapshot, { enabled: open, baselineKey: seedVersion });
	// Cancel, the ✕, the mask and Esc all ask first when something was typed.
	const handleCancel = useCallback(() => guardDiscard(() => onCancel?.()), [guardDiscard, onCancel]);

	const updateRow = useCallback((elementName, patch) => {
		setRows(prev => ({ ...prev, [elementName]: { ...(prev[elementName] || {}), ...patch } }));
		setPreview(null);
		setFormError(null);
	}, []);

	// {{source.*}} is readable in a profile — and in a template the API
	// already calls one (a formula reads the source without copyAll), so
	// editing it never strands its own formulas.
	const sourceAllowed = profile || (mode === 'edit' && template?.duplicateProfile === true);

	/**
	 * Turning profile mode on starts every copyable row that sets nothing at
	 * Copy (the profile copies everything unless told otherwise); turning it
	 * off returns Copy rows to Skip — a plain quick add has nothing to copy.
	 */
	const handleProfileChange = useCallback((next) => {
		setProfile(next);
		setCopyAll(next);
		if (!next) setDefaultForDuplicate(false);
		setRows(prev => {
			const out = {};
			editableElements.forEach(el => {
				const row = prev[el.name] || { mode: ROW_MODE.SKIP, value: undefined, formula: '', blanks: 'empty', transform: '' };
				if (next && row.mode === ROW_MODE.SKIP && isCopyable(el)) out[el.name] = { ...row, mode: ROW_MODE.COPY };
				else if (!next && row.mode === ROW_MODE.COPY) out[el.name] = { ...row, mode: ROW_MODE.SKIP };
				else out[el.name] = row;
			});
			return out;
		});
		setPreview(null);
		setFormError(null);
	}, [editableElements]);

	// The Fixed values, as the stub event the element inputs read (a lookup
	// input needs the collection; nothing else reads the record).
	const staticValues = useMemo(() => {
		const out = {};
		Object.entries(rows).forEach(([elementName, row]) => {
			if (row?.mode === ROW_MODE.STATIC) out[elementName] = row.value;
		});
		return out;
	}, [rows]);
	const stubEvent = useMemo(() => ({ collection_id: collectionId, elements: staticValues }), [collectionId, staticValues]);

	// The names of the Calculated rows, as one key: the token trees and the
	// analyses below depend on WHICH rows are formulas, not on every
	// keystroke in any row — a hundred-row template must not re-derive a
	// hundred token trees per character typed.
	const formulaNames = useMemo(
		() => editableElements.filter(el => rows[el.name]?.mode === ROW_MODE.FORMULA).map(el => el.name),
		[editableElements, rows]
	);
	const formulaNamesKey = formulaNames.join('\u0000');

	// Live analysis of every Calculated row, against the template's roots.
	// Each row's analysis is cached on its own formula text (and the card
	// vocabulary it was analysed against), so typing in one row re-analyses
	// that row alone.
	const analysisCacheRef = useRef({ vocab: null, byName: new Map() });
	const analyses = useMemo(() => {
		const cache = analysisCacheRef.current;
		const vocab = { mergedElements, getElementsForCard };
		if (cache.vocab?.mergedElements !== mergedElements || cache.vocab?.getElementsForCard !== getElementsForCard) {
			cache.vocab = vocab;
			cache.byName = new Map();
		}
		const out = {};
		const byName = new Map(editableElements.map(el => [el.name, el]));
		formulaNames.forEach(name => {
			const el = byName.get(name);
			const formula = rows[name]?.formula || '';
			const hit = cache.byName.get(name);
			if (hit && hit.formula === formula && hit.element === el && hit.sourceAllowed === sourceAllowed) {
				out[name] = hit.analysis;
				return;
			}
			// A lookup's {{me.id}} is the caller's own record of its card
			// (utils/lookupMe.js): settled before analysis, like {{user.id}}.
			const meIdOnLookup = el?.dataType === 'card-lookup' && isMeIdFormula(formula) && !elementNames.has('me');
			// A user element's only formula is the caller (the API's rule):
			// exactly {{user.id}}, and nothing computes an account.
			const analysis = meIdOnLookup
				? (lookupMeAvailable(el)
					? { ok: true, empty: false, canonical: ME_ID_TOKEN, typeLabel: 'your own record', target: { ok: true, transforms: ['none'], note: 'Filled with the record linked to whoever launches the quick add; left blank when they have none.' }, refs: [], ownRefs: [], walks: [] }
					: { ok: false, empty: false, error: { message: `${ME_ID_TOKEN} is your own record of the lookup card, and no record of that card can belong to anyone yet — the card needs a User element marked as its account link.`, position: null } })
				: el?.dataType === 'user'
				? (isUserIdFormula(formula)
					? { ok: true, empty: false, canonical: USER_ID_TOKEN, typeLabel: 'the signed-in user', target: { ok: true, transforms: ['none'], note: 'Stored as the account of whoever launches the quick add.' }, refs: [], ownRefs: [], walks: [] }
					: (formula.trim()
						? { ok: false, empty: false, error: { message: `A User element takes exactly ${USER_ID_TOKEN} (the signed-in user); nothing else computes an account.`, position: null } }
						: { ok: false, empty: true, error: null }))
				: analyzeFormula(formula, {
					mergedElements,
					getElementsForCard,
					targetElement: el,
					resolveRef: createQuickAddRefResolver(mergedElements, getElementsForCard, { excludeName: name, meElements, source: sourceAllowed }),
				});
			cache.byName.set(name, { formula, element: el, analysis, sourceAllowed });
			out[name] = analysis;
		});
		return out;
	}, [editableElements, formulaNames, rows, mergedElements, getElementsForCard, meElements, lookupMeAvailable, elementNames, sourceAllowed]);

	const tokensByElement = useMemo(() => {
		const out = {};
		const byName = new Map(editableElements.map(el => [el.name, el]));
		formulaNames.forEach(name => {
			const target = byName.get(name) || null;
			out[name] = buildQuickAddTokens(mergedElements, getElementsForCard, { excludeName: name, meElements, targetElement: target, lookupMe: lookupMeAvailable(target), source: sourceAllowed });
		});
		return out;
	}, [formulaNamesKey, mergedElements, getElementsForCard, meElements, lookupMeAvailable, sourceAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

	const functionGroups = useMemo(functionOptions, []);

	const usesLastItem = useMemo(
		() => editableElements.some(el => rows[el.name]?.mode === ROW_MODE.FORMULA
			&& readsLastItem(analyses[el.name], rows[el.name]?.formula, elementNames)),
		[editableElements, rows, analyses, elementNames]
	);

	const matchOptions = useMemo(() => editableElements
		.filter(el => rows[el.name]?.mode === ROW_MODE.STATIC && MATCHABLE_TYPES.has(el.dataType))
		.map(el => ({ value: el.name, label: labelOf(el) })), [editableElements, rows]);

	/**
	 * The wire body, or `{ error }` naming what still needs fixing. Fixed rows
	 * must hold a value (the API refuses an empty static — Skip is the way to
	 * say "leave it"), Calculated rows must analyse clean.
	 */
	const buildBody = useCallback(() => {
		const defaults = {};
		// A {{source.*}}-only profile stays one until a row is set to Copy;
		// then it copies everything else too, and its Skips say so.
		const copiesAll = profile && (copyAll || editableElements.some(el => rows[el.name]?.mode === ROW_MODE.COPY));
		for (const el of editableElements) {
			const row = rows[el.name];
			// Copy is the profile's absence of a default; Skip in a profile is
			// the explicit "leave blank" (an element the card already leaves
			// blank on duplicate needs no word for it).
			if (!row || row.mode === ROW_MODE.COPY) continue;
			if (row.mode === ROW_MODE.SKIP) {
				if (copiesAll && isCopyable(el)) defaults[el.name] = { kind: 'skip' };
				continue;
			}
			const storedPreset = mode === 'edit' && template?.defaults?.[el.name]?.kind === 'static' ? template.defaults[el.name].value : undefined;
			if (row.mode === ROW_MODE.STATIC && isRepeatingGroup(el) && storedPreset !== undefined && row.value === storedPreset) {
				// Untouched: sent back exactly as served, the API's echo — a bound
				// or a rule added since never refuses an edit of something else.
				defaults[el.name] = { kind: 'static', value: storedPreset };
			} else if (row.mode === ROW_MODE.STATIC && isRepeatingGroup(el)) {
				const preset = presetForWire(el, row.value, { zone: zoneRef.current || undefined });
				if (preset.error) return { error: preset.error, element: el.name };
				defaults[el.name] = { kind: 'static', value: preset.value };
			} else if (row.mode === ROW_MODE.STATIC) {
				if (isElementValueEmpty(el, row.value)) {
					return { error: `${labelOf(el)}: pick a value, or switch it to Skip.`, element: el.name };
				}
				defaults[el.name] = { kind: 'static', value: row.value };
			} else if (isRepeatingGroup(el)) {
				return { error: `${labelOf(el)}: a list takes fixed entries — switch it to Fixed or Skip.`, element: el.name };
			} else {
				const analysis = analyses[el.name];
				if (!analysis?.ok) {
					return {
						error: `${labelOf(el)}: ${analysis?.empty ? 'write a formula, or switch it to Skip.' : (analysis?.error?.message || 'the formula is not valid yet.')}`,
						element: el.name,
					};
				}
				defaults[el.name] = {
					kind: 'formula',
					formula: analysis.canonical,
					blanks: row.blanks === 'zero' ? 'zero' : 'empty',
					transform: { type: row.transform || (analysis.target.transforms || ['none'])[0] || 'none' },
				};
			}
		}
		const matchable = new Set(matchOptions.map(opt => opt.value));
		const matchElements = (lastItem.matchElements || []).filter(name => matchable.has(name));
		const body = {
			card_id: activeCardId,
			defaults,
			lastItem: usesLastItem && (matchElements.length || lastItem.mine) ? { matchElements, mine: !!lastItem.mine } : null,
		};
		// Sent when it says something: on, or turning a stored copyAll off.
		if (copiesAll || (mode === 'edit' && template?.copyAll === true)) body.copyAll = copiesAll;
		// Its tags (T9): the whole list; the API judges only what it adds.
		if (tagIds.length || (mode === 'edit' && Array.isArray(template?.tagIds) && template.tagIds.length)) body.tagIds = tagIds;
		return { body };
	}, [editableElements, rows, analyses, matchOptions, lastItem, activeCardId, usesLastItem, profile, copyAll, mode, template?.copyAll, template?.tagIds, template?.defaults, tagIds]);

	const handlePreview = async () => {
		const { body, error } = buildBody();
		if (error) { setFormError(error); return; }
		setPreviewing(true);
		setFormError(null);
		try {
			setPreview(await previewQuickAddTemplate(collectionId, body));
		} catch (err) {
			setPreview(null);
			setFormError(err?.response?.data?.message || 'Could not preview this quick add.');
		} finally {
			setPreviewing(false);
		}
	};

	const ops = useMemo(() => ({
		create: draft => (store?.create ? store.create(draft) : createQuickAddTemplate(collectionId, draft)),
		update: (target, patch) => (store?.update ? store.update(target, patch) : updateQuickAddTemplate(collectionId, normalizeId(target?._id), patch)),
		promote: target => (store?.promote ? store.promote(target) : promoteQuickAddTemplate(collectionId, normalizeId(target?._id))),
		demote: target => (store?.demote ? store.demote(target) : demoteQuickAddTemplate(collectionId, normalizeId(target?._id))),
	}), [store, collectionId]);

	/** Tier moves are their own endpoints; a failure there leaves the save itself intact. */
	const moveTier = async (saved, wanted) => {
		const current = saved?.scope === QUICK_ADD_SCOPE.COLLECTION ? QUICK_ADD_SCOPE.COLLECTION : QUICK_ADD_SCOPE.USER;
		if (!isManager || current === wanted) return saved;
		try {
			const moved = await (wanted === QUICK_ADD_SCOPE.COLLECTION ? ops.promote(saved) : ops.demote(saved));
			// A shared quick add leaves its maker's private tags behind (T9).
			if (moved?.tagsLeftOff) {
				message.info(`${moved.tagsLeftOff === 1 ? 'Your private tag stays' : 'Your private tags stay'} yours: the quick add for everyone leaves ${moved.tagsLeftOff === 1 ? 'it' : 'them'} off.`);
			}
			return moved || saved;
		} catch (err) {
			message.warning(err?.response?.data?.message || (wanted === QUICK_ADD_SCOPE.COLLECTION
				? 'Saved for you, but it could not be pinned for everyone.'
				: 'Saved, but it could not be unpinned.'));
			return saved;
		}
	};

	/**
	 * The card's default for Duplicate is a collection-tier flag, so it is
	 * set after the tier move (the API refuses it on a personal template);
	 * a failure leaves the save itself intact.
	 */
	const applyDefaultForDuplicate = async (saved) => {
		const wanted = isManager && profile && tier === QUICK_ADD_SCOPE.COLLECTION && defaultForDuplicate;
		const current = saved?.defaultForDuplicate === true;
		if (!isManager || wanted === current || saved?.scope !== QUICK_ADD_SCOPE.COLLECTION) return saved;
		try {
			const next = await ops.update(saved, { defaultForDuplicate: wanted });
			return { ...saved, ...(next || {}), defaultForDuplicate: wanted };
		} catch (err) {
			message.warning(err?.response?.data?.message || 'Saved, but the default for Duplicate could not be changed.');
			return saved;
		}
	};

	const handleSave = async () => {
		const trimmed = name.trim();
		if (!trimmed) { setNameError('Give the quick add a name.'); return; }
		if (!activeCardId) { setFormError('Pick a card first.'); return; }
		const { body, error } = buildBody();
		if (error) { setFormError(error); return; }
		setSaving(true);
		setNameError(null);
		setFormError(null);
		try {
			let saved;
			if (mode === 'edit' && template) {
				const patch = { name: trimmed, defaults: body.defaults, lastItem: body.lastItem };
				if (body.copyAll !== undefined) patch.copyAll = body.copyAll;
				if (body.tagIds !== undefined) patch.tagIds = body.tagIds;
				saved = await ops.update(template, patch);
				saved = { ...template, ...(saved || {}) };
			} else {
				saved = await ops.create({ name: trimmed, ...body });
			}
			saved = await moveTier(saved, tier);
			saved = await applyDefaultForDuplicate(saved);
			message.success(mode === 'edit' ? 'Quick add saved.' : 'Quick add created.');
			resetDirty();
			if (typeof onSaved === 'function') onSaved(saved);
		} catch (err) {
			const status = err?.response?.status;
			if (status === 409) setNameError(DUPLICATE_NAME_MESSAGE);
			else setFormError(err?.response?.data?.message || 'Could not save the quick add.');
		} finally {
			setSaving(false);
		}
	};

	// The preview's lookup values are item ids; they render as the referenced
	// items' titles the way every read surface does — the resolved bag
	// stands in as a one-item pool for useLinkedItems (which batch-fetches
	// what it references, nested titles included) and createLookupResolver
	// walks that pool. Keyed on the card so a create-mode pick resets the
	// pool; a re-preview re-seeds it.
	const previewSeed = useMemo(() => (
		preview?.elements && activeCardId
			? [{ _id: 'quick-add-preview', card_id: activeCardId, elements: preview.elements }]
			: []
	), [preview, activeCardId]);
	const previewItemsById = useLinkedItems({
		items: previewSeed,
		getElementsForCard,
		enabled: previewSeed.length > 0,
		resetKey: activeCardId,
	});
	const resolvePreviewLookup = useMemo(() => createLookupResolver({
		itemsById: previewItemsById,
		cardsById,
		getElementsForCard,
	}), [previewItemsById, cardsById, getElementsForCard]);

	const previewRows = useMemo(() => {
		if (!preview) return [];
		const values = preview.elements || {};
		return mergedElements
			.filter(el => Object.prototype.hasOwnProperty.call(values, el.name))
			.map(el => ({
				name: el.name,
				label: labelOf(el),
				text: el.dataType === 'card-lookup'
					? (resolvePreviewLookup(values[el.name]) || '—')
					: (isRepeatingGroup(el) ? presetSummary(el, values[el.name]) : formatCalculatedValue(el, values[el.name])),
			}));
	}, [preview, mergedElements, resolvePreviewLookup]);

	const title = mode === 'edit' ? 'Edit quick add' : (mode === 'capture' ? 'Save as quick add' : 'New quick add');

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			title={title}
			width={720}
			destroyOnClose
			className="quick-add-modal"
			footer={(
				<Space wrap>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button onClick={handlePreview} loading={previewing} disabled={!activeCardId || saving}>Preview</Button>
					<Button type="primary" onClick={handleSave} loading={saving} disabled={!activeCardId || previewing}>
						{mode === 'edit' ? 'Save' : 'Create'}
					</Button>
				</Space>
			)}
		>
			<Space direction="vertical" size={16} style={{ width: '100%' }}>

				{mode === 'create' ? (
					<div>
						<label htmlFor="quick-add-card" className="d-block">Card</label>
						<Select
							id="quick-add-card"
							style={{ width: '100%' }}
							placeholder="Select a card"
							aria-label="Card"
							value={cardId}
							onChange={value => { setCardId(value); setPreview(null); setFormError(null); }}
							options={(cardOptions || []).map(opt => ({ ...opt, label: <CardOptionLabel card={opt.card} depth={opt.depth} /> }))}
							showSearch
							filterOption={optionSearchFilter}
							getPopupContainer={trigger => trigger.parentElement || document.body}
							virtual={false}
						/>
					</div>
				) : (activeCard ? (
					<div className="modal-identity">
						<RecordIdentity
							kicker="Card"
							badge={<RecordBadge kind="card" record={activeCard} title={activeCard?.name || 'Untitled card'} />}
						/>
					</div>
				) : null)}

				<div>
					<label htmlFor="quick-add-name" className="d-block">Name</label>
					<Input
						id="quick-add-name"
						autoFocus={mode !== 'create'}
						maxLength={100}
						value={name}
						placeholder="e.g. Molly seated BP"
						onChange={event => { setName(event.target.value); setNameError(null); }}
						onPressEnter={handleSave}
						status={nameError ? 'error' : undefined}
					/>
					{nameError ? <div className="saved-filters__name-error" role="alert">{nameError}</div> : null}
				</div>

				{isManager ? (
					<div>
						<label className="d-block" id="quick-add-tier-label">Who can use it</label>
						<Segmented
							aria-labelledby="quick-add-tier-label"
							value={tier}
							onChange={setTier}
							options={TIER_OPTIONS}
						/>
					</div>
				) : null}

				<div className="quick-add-modal__profile">
					<div className="toggle-row">
						<div className="toggle-row__text">
							<span className="toggle-row__label" id="quick-add-profile-label">Duplicate profile</span>
							<span className="toggle-row__description">
								{profile && !copyAll && !editableElements.some(el => rows[el.name]?.mode === ROW_MODE.COPY)
									? <>Copies only what the rows below read from the item being duplicated ({'{{source.…}}'}). Set a row to Copy to copy the rest as well. Offered from an item&rsquo;s Duplicate menu instead of Add item.</>
									: <>Copy everything from the item being duplicated, except what you set below. Offered from an item&rsquo;s Duplicate menu instead of Add item.</>}
							</span>
						</div>
						<Switch
							checked={profile}
							onChange={handleProfileChange}
							aria-labelledby="quick-add-profile-label"
						/>
					</div>
					{profile && isManager ? (
						<div className="toggle-row">
							<div className="toggle-row__text">
								<span className="toggle-row__label" id="quick-add-default-label">Default for Duplicate</span>
								<span className="toggle-row__description">
									{tier === QUICK_ADD_SCOPE.COLLECTION
										? `A plain click on Duplicate uses this profile for every ${activeCard?.name || 'item'}. Replaces the card's current default.`
										: 'Set "Who can use it" to Everyone first — only a shared profile can be the default.'}
								</span>
							</div>
							<Switch
								checked={defaultForDuplicate && tier === QUICK_ADD_SCOPE.COLLECTION}
								disabled={tier !== QUICK_ADD_SCOPE.COLLECTION}
								onChange={setDefaultForDuplicate}
								aria-labelledby="quick-add-default-label"
							/>
						</div>
					) : null}
				</div>

				{activeCard ? (
					<section className="quick-add-modal__panel">
						<div className="micro-label form-panel__kicker">Elements</div>
						{editableElements.length ? (
							<div className="quick-add-rows">
								{editableElements.map(el => {
									const row = rows[el.name] || { mode: ROW_MODE.SKIP };
									const label = labelOf(el);
									const offersCopy = profile && isCopyable(el);
									return (
										<div key={el.name} className={`quick-add-row quick-add-row--${row.mode}`} data-element={el.name}>
											<div className="quick-add-row__head">
												<span className="quick-add-row__label">{label}</span>
												<Segmented
													size="small"
													aria-label={`${label} default`}
													value={row.mode}
													onChange={value => updateRow(el.name, { mode: value })}
													options={modeOptionsFor(el, offersCopy)}
												/>
											</div>
											{profile && !isCopyable(el) && row.mode === ROW_MODE.SKIP ? (
												<Typography.Text type="secondary" className="quick-add-row__note d-block">
													{notCopiedReason(el)}
												</Typography.Text>
											) : null}
											{row.mode === ROW_MODE.STATIC ? (
												<div className={`quick-add-row__control quick-add-row__control--fixed${isRepeatingGroup(el) ? ' quick-add-row__control--entries' : ''}`}>
													<EventFormElement
														event={stubEvent}
														element={isRepeatingGroup(el) ? presetGroup(el) : el}
														value={row.value}
														onChange={(elementName, value) => updateRow(elementName, { value })}
														cardMeta={activeCard}
														showIconBadge={false}
														zone={isRepeatingGroup(el) ? (zoneRef.current || undefined) : undefined}
													/>
												</div>
											) : null}
											{row.mode === ROW_MODE.STATIC && isRepeatingGroup(el) ? (
												<Typography.Text type="secondary" className="quick-add-row__note d-block">
													Each new item starts with these entries. Whoever adds it can change, add or remove them before saving.
												</Typography.Text>
											) : null}
											{row.mode === ROW_MODE.FORMULA ? (
												<div className="quick-add-row__control">
													<FormulaRow
														element={el}
														row={row}
														analysis={analyses[el.name] || { ok: false, empty: true }}
														tokens={tokensByElement[el.name] || []}
														functionGroups={functionGroups}
														onChange={patch => updateRow(el.name, patch)}
													/>
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						) : (
							<Typography.Text type="secondary">This card has no elements a template can set.</Typography.Text>
						)}
					</section>
				) : null}

				{activeCard ? (
					<TemplateTagsField
						id="quick-add-tags"
						collectionId={collectionId}
						collection={collection}
						value={tagIds}
						onChange={next => { setTagIds(next); setPreview(null); setFormError(null); }}
						allowPrivate={tier !== QUICK_ADD_SCOPE.COLLECTION && !(mode === 'edit' && template?.scope === QUICK_ADD_SCOPE.COLLECTION)}
						extra={tier === QUICK_ADD_SCOPE.COLLECTION
							? 'Put on each item it adds. The form shows them before saving. Private tags stay yours, so a quick add for everyone leaves them off.'
							: 'Put on each item it adds. The form shows them before saving.'}
					/>
				) : null}

				{usesLastItem ? (
					<section className="quick-add-modal__panel">
						<div className="micro-label form-panel__kicker">Last item</div>
						<Typography.Text type="secondary" className="d-block">
							A calculated default reads the most recent {activeCard?.name || 'item'} you can see. Narrow which one counts:
						</Typography.Text>
						<div className="quick-add-modal__last-item">
							<div>
								<label htmlFor="quick-add-match" className="d-block">Match the last item on</label>
								<Select
									id="quick-add-match"
									mode="multiple"
									style={{ width: '100%' }}
									placeholder={matchOptions.length ? 'Any item of the card' : 'Set a Fixed lookup, choice or text first'}
									aria-label="Match the last item on"
									value={(lastItem.matchElements || []).filter(name => matchOptions.some(opt => opt.value === name))}
									onChange={matchElements => { setLastItem(prev => ({ ...prev, matchElements })); setPreview(null); }}
									options={matchOptions}
									disabled={!matchOptions.length}
									getPopupContainer={trigger => trigger.parentElement || document.body}
								/>
							</div>
							<div className="toggle-row">
								<div className="toggle-row__text">
									<span className="toggle-row__label">Only items I added</span>
								</div>
								<Switch
									checked={!!lastItem.mine}
									onChange={mine => { setLastItem(prev => ({ ...prev, mine })); setPreview(null); }}
									aria-label="Only items I added"
								/>
							</div>
						</div>
					</section>
				) : null}

				{formError ? <Alert type="error" showIcon message={formError} role="alert" /> : null}

				{preview ? (
					<section className="quick-add-modal__panel quick-add-modal__preview" aria-live="polite">
						<div className="micro-label form-panel__kicker">Preview</div>
						{previewRows.length ? (
							<dl className="quick-add-modal__preview-list">
								{previewRows.map(entry => (
									<div key={entry.name} className="quick-add-modal__preview-row">
										<dt>{entry.label}</dt>
										<dd>{entry.text}</dd>
									</div>
								))}
							</dl>
						) : (
							<Typography.Text type="secondary">Nothing would be filled in right now.</Typography.Text>
						)}
						{Array.isArray(preview.warnings) && preview.warnings.length ? (
							<ul className="quick-add-modal__warnings">
								{[...new Set(preview.warnings.map(w => (w?.element ? `${w.element}: ${w.message}` : w?.message)).filter(Boolean))].map(text => (
									<li key={text}><Typography.Text type="warning">{text}</Typography.Text></li>
								))}
							</ul>
						) : null}
					</section>
				) : null}

			</Space>
		</Modal>
	);
}
